import { FormProvider, SubmitHandler } from 'react-hook-form'
import { t } from '@lingui/macro'
import { Grid } from '@mui/material'

import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import { UpdatePatientDto } from '@/core/models/patient.model'
import { SavedAddress } from '@/core/types/shared'

import { useIsSA } from '@/hooks/useIsSA'

import { PatientProfileFormData, usePatientProfileForm, UsePatientProfileFormProps } from '../model/usePatientProfileForm'
import { InsInfoSection } from './InsInfoSection'
import { GeneralInfoSection } from './GeneralInfoSection'
import { BillingInfoSection } from './BillingInfoSection'
import { MedicalInfoSection } from './MedicalInfoSection'
import { AddressInfoSection } from './AddressInfoSection'
import { SavedAddressesSection } from './SavedAddressesSection'
import { ConsentSection } from './ConsentSection'
import { PrivateDoctorSection } from './PrivateDoctorSection'
import { ParentsSection } from './ParentsSection'
import { PregnancySection } from '@/features/pregnancy'
import { useCreatePatient } from '../model/useCreatePatient'
import { useUpdatePatient } from '../model/useUpdatePatient'
import { Dispatch, SetStateAction } from 'react'

type CommonProps = {
  action?: ({ isSubmitting }: { isSubmitting: boolean }) => React.ReactNode
  actionPlacement?: 'top' | 'bottom'
  editable?: boolean
  intro?: boolean
  isQualified?: boolean
  onSubmit?: SubmitHandler<PatientProfileFormData>
  savedAddresses?: SavedAddress[]
}

export type PatientProfileFormProps =
  | (UsePatientProfileFormProps & CommonProps & { mode: 'create'; onUpdate?: never; patientId?: never; setEditable?: never })
  | (UsePatientProfileFormProps &
      CommonProps & { mode: 'edit'; onUpdate?: (dto: UpdatePatientDto) => void; patientId: string; setEditable: Dispatch<SetStateAction<boolean>> })

export function PatientProfileForm(props: PatientProfileFormProps) {
  const {
    action,
    actionPlacement = 'bottom',
    editable: editableProp = false,
    errors,
    initialValues,
    intro = false,
    isQualified = false,
    mode,
    onSubmit: onSubmitProp,
    savedAddresses,
    setEditable,
  } = props
  const { isINSEnabled = false, rhForm, ssnLength } = usePatientProfileForm({ initialValues, errors })
  // SEHA (en-SA) form: no billing, no patient agreement, no primary care physician.
  const isSA = useIsSA()

  const [createPatient, isCreating] = useCreatePatient(intro)
  const [updatePatient, isUpdating] = useUpdatePatient({ onUpdate: props.onUpdate })

  const onSubmit: SubmitHandler<PatientProfileFormData> = async (data) => {
    if (onSubmitProp) {
      onSubmitProp(data)
      return
    }

    switch (mode) {
      case 'create':
        await createPatient(data)
        return
      case 'edit':
        await updatePatient(data, props.patientId)
        setEditable(false)
        return
      default:
        return
    }
  }

  const editable = editableProp || mode === 'create'
  const isSubmitting = isCreating || isUpdating

  return (
    <FormProvider {...rhForm}>
      <form onSubmit={rhForm.handleSubmit(onSubmit)} id="patientForm">
        {actionPlacement === 'top' && action?.({ isSubmitting })}
        <Grid container rowSpacing={3} columnSpacing={10}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {isINSEnabled ? (
                <Grid item xs={12}>
                  <InsInfoSection editable={editable} isQualified={isQualified} />
                </Grid>
              ) : (
                <Grid id="patientInformation" item xs={12}>
                  <GeneralInfoSection editable={editable} isINSEnabled={isINSEnabled} mode={mode} ssnLength={ssnLength} />
                </Grid>
              )}
              {!isSA && (
                <Grid item xs={12}>
                  <BillingInfoSection editable={editable} />
                </Grid>
              )}
              <Grid item xs={12}>
                <MedicalInfoSection editable={editable} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {isINSEnabled && (
                <Grid id="patientInformation" item xs={12}>
                  <GeneralInfoSection editable={editable} isINSEnabled={isINSEnabled} mode={mode} ssnLength={ssnLength} />
                </Grid>
              )}
              <Grid id="adress-information" item xs={12}>
                <AddressInfoSection editable={editable} />
              </Grid>
              {mode === 'edit' && savedAddresses?.length ? (
                <Grid id="saved-addresses" item xs={12}>
                  <SavedAddressesSection savedAddresses={savedAddresses} />
                </Grid>
              ) : null}
              {mode === 'create' && !isSA && (
                <Grid item id="agreement" xs={12}>
                  <ConsentSection />
                </Grid>
              )}
              {!isSA && (
                <Grid id="extra-info" item xs={12}>
                  <PrivateDoctorSection editable={editable} mode={mode} />
                </Grid>
              )}
              <Grid item xs={12}>
                <ParentsSection readOnly={!editable} />
              </Grid>
              {mode === 'edit' && props.patientId && (
                <Grid item xs={12}>
                  <PregnancySection patientId={props.patientId} readOnly={!editable} />
                </Grid>
              )}
              {mode === 'create' && (
                <Grid item xs={12}>
                  <CheckboxInputController
                    control={rhForm.control}
                    label={t`I confirm that the patient's identity and information are correct`}
                    name="identityConfirmed"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        {actionPlacement === 'bottom' && action?.({ isSubmitting })}
      </form>
    </FormProvider>
  )
}
